/**
 * Payment Management Page
 * 
 * Displays and manages participant-level payments with filtering, retry functionality,
 * and detailed payment information including installment history.
 */

import { useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  DataTable,
  TextInput,
  Select,
  Badge
} from '@etherisc/ui-kit';
import { 
  Search, 
  RefreshCw, 
  Eye, 
  RotateCcw,
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import { useParticipantPayments, useParticipantPaymentDetails, useRetryPayment } from '../../hooks/useParticipantPayments';
import { participantPaymentService, ParticipantPayment, PaymentInstallment } from '../../services/participantPaymentService';
import { formatDate } from '../../utils/formatters';
import { PermissionGate } from '../../components/PermissionGate';

// Type definitions for table cell components
interface TableCellProps {
  row: {
    original: ParticipantPayment;
  };
}

interface InstallmentTableCellProps {
  row: {
    original: PaymentInstallment;
  };
}

export function PaymentManagement() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const {
    payments,
    loading,
    error,
    pagination,
    refreshPayments,
    updateFilters,
    changePage,
    changeLimit
  } = useParticipantPayments({
    page: 1,
    limit: 25
  });

  const { paymentDetails, loading: detailsLoading } = useParticipantPaymentDetails(selectedPaymentId || undefined);
  const { retryPayment, loading: retryLoading } = useRetryPayment();

  // Handle search
  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    updateFilters({ 
      page: 1, 
      participantName: value || undefined,
      quoteNumber: value || undefined 
    });
  }, [updateFilters]);

  // Handle status filter
  const handleStatusFilter = useCallback((value: string) => {
    setStatusFilter(value);
    updateFilters({ 
      page: 1, 
      status: value === 'all' ? undefined : value 
    });
  }, [updateFilters]);

  // Handle view payment details
  const handleViewPayment = useCallback((paymentId: string) => {
    setSelectedPaymentId(paymentId);
    setShowDetailsModal(true);
  }, []);

  // Handle retry payment
  const handleRetryPayment = useCallback(async (paymentId: string) => {
    const success = await retryPayment(paymentId);
    if (success) {
      refreshPayments();
    }
  }, [retryPayment, refreshPayments]);

  // Handle close modal
  const handleCloseModal = useCallback(() => {
    setShowDetailsModal(false);
    setSelectedPaymentId(null);
  }, []);

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'authorized':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'partial':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'pending':
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  // Table columns configuration
  const columns = [
    {
      accessorKey: 'quoteNumber',
      header: 'Quote Number',
      cell: ({ row }: TableCellProps) => (
        <div className="font-medium">
          {row.original.quoteNumber}
        </div>
      )
    },
    {
      accessorKey: 'participantName',
      header: 'Participant',
      cell: ({ row }: TableCellProps) => (
        <div>
          <div className="font-medium">{row.original.participantName}</div>
          <div className="text-sm text-gray-500 capitalize">
            {row.original.participantType}
          </div>
        </div>
      )
    },
    {
      accessorKey: 'totalAmountCents',
      header: 'Amount Due',
      cell: ({ row }: TableCellProps) => (
        <div className="font-medium">
          {participantPaymentService.formatAmount(row.original.totalAmountCents)}
        </div>
      )
    },
    {
      accessorKey: 'paidAmountCents',
      header: 'Amount Paid',
      cell: ({ row }: TableCellProps) => (
        <div className="font-medium text-green-600">
          {participantPaymentService.formatAmount(row.original.paidAmountCents)}
        </div>
      )
    },
    {
      accessorKey: 'remainingAmountCents',
      header: 'Remaining',
      cell: ({ row }: TableCellProps) => (
        <div className="font-medium text-orange-600">
          {participantPaymentService.formatAmount(row.original.remainingAmountCents)}
        </div>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }: TableCellProps) => (
        <div className="flex items-center gap-2">
          {getStatusIcon(row.original.status)}
          <Badge variant={participantPaymentService.getStatusColor(row.original.status)}>
            {participantPaymentService.getStatusText(row.original.status)}
          </Badge>
        </div>
      )
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }: TableCellProps) => (
        <div className="text-sm text-gray-600">
          {formatDate(row.original.createdAt)}
        </div>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }: TableCellProps) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleViewPayment(row.original.id)}
          >
            <Eye className="h-4 w-4" />
          </Button>
          {row.original.status === 'failed' && (
            <PermissionGate permission="payments:update">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRetryPayment(row.original.id)}
                disabled={retryLoading}
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            </PermissionGate>
          )}
        </div>
      )
    }
  ];

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-red-600">
              <XCircle className="h-12 w-12 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Error Loading Payments</h3>
              <p>{error}</p>
              <Button onClick={refreshPayments} className="mt-4">
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Payment Management</h1>
          <p className="text-gray-600 mt-1">
            Manage participant-level payments and track payment status
          </p>
        </div>
        <Button onClick={refreshPayments} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-base-content/50 z-10" />
              <div className="pl-10">
                <TextInput
                  placeholder="Search by quote number or participant name..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
            </div>
            <Select
              label="Status"
              value={statusFilter}
              onValueChange={handleStatusFilter}
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'pending', label: 'Pending' },
                { value: 'authorized', label: 'Authorized' },
                { value: 'partial', label: 'Partial' },
                { value: 'completed', label: 'Completed' },
                { value: 'failed', label: 'Failed' }
              ]}
            />
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                  updateFilters({ page: 1, status: undefined, participantName: undefined, quoteNumber: undefined });
                }}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Participant Payments ({pagination.total})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={payments}
            loading={loading}
            enableSorting={true}
            enableResizing={true}
            manualPagination={true}
            pageCount={pagination.totalPages}
            rowCount={pagination.total}
            onPaginationChange={(paginationState) => {
              const newPage = paginationState.pageIndex + 1; // Convert 0-based to 1-based
              const newPageSize = paginationState.pageSize;
              
              changePage(newPage);
              if (newPageSize !== pagination.limit) {
                changeLimit(newPageSize);
              }
            }}
            state={{
              pagination: {
                pageIndex: pagination.page - 1, // Convert 1-based to 0-based
                pageSize: pagination.limit,
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Payment Details Modal */}
      {showDetailsModal && paymentDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">Payment Details</h3>
              <Button variant="ghost" onClick={handleCloseModal}>
                <XCircle className="h-5 w-5" />
              </Button>
            </div>
          
          {detailsLoading ? (
            <div className="flex items-center justify-center p-8">
              <RefreshCw className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Payment Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Payment Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Quote:</span>
                      <span className="font-medium">{paymentDetails.quoteNumber}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Participant:</span>
                      <span className="font-medium">{paymentDetails.participantName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <Badge variant={participantPaymentService.getStatusColor(paymentDetails.status)}>
                        {participantPaymentService.getStatusText(paymentDetails.status)}
                      </Badge>
                    </div>
                    {paymentDetails.authorizationId && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Authorization ID:</span>
                        <span className="font-mono text-sm">{paymentDetails.authorizationId}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Amount Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Due:</span>
                      <span className="font-medium text-lg">
                        {participantPaymentService.formatAmount(paymentDetails.totalAmountCents)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Amount Paid:</span>
                      <span className="font-medium text-green-600">
                        {participantPaymentService.formatAmount(paymentDetails.paidAmountCents)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Remaining:</span>
                      <span className="font-medium text-orange-600">
                        {participantPaymentService.formatAmount(paymentDetails.remainingAmountCents)}
                      </span>
                    </div>
                    <div className="border-t pt-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Progress:</span>
                        <span className="font-medium">
                          {Math.round((paymentDetails.paidAmountCents / paymentDetails.totalAmountCents) * 100)}%
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Payment Installments */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Payment History ({paymentDetails.installments.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {paymentDetails.installments.length > 0 ? (
                    <DataTable
                      columns={[
                        {
                          accessorKey: 'createdAt',
                          header: 'Date',
                          cell: ({ row }: InstallmentTableCellProps) => formatDate(row.original.createdAt)
                        },
                        {
                          accessorKey: 'amountCents',
                          header: 'Amount',
                          cell: ({ row }: InstallmentTableCellProps) => (
                            <span className="font-medium">
                              {participantPaymentService.formatAmount(row.original.amountCents)}
                            </span>
                          )
                        },
                        {
                          accessorKey: 'status',
                          header: 'Status',
                          cell: ({ row }: InstallmentTableCellProps) => (
                            <Badge variant={participantPaymentService.getStatusColor(row.original.status)}>
                              {participantPaymentService.getStatusText(row.original.status)}
                            </Badge>
                          )
                        },
                        {
                          accessorKey: 'transactionId',
                          header: 'Transaction ID',
                          cell: ({ row }: InstallmentTableCellProps) => (
                            <span className="font-mono text-sm">
                              {row.original.transactionId || 'N/A'}
                            </span>
                          )
                        },
                        {
                          accessorKey: 'processedAt',
                          header: 'Processed At',
                          cell: ({ row }: InstallmentTableCellProps) => (
                            <span className="text-sm">
                              {row.original.processedAt ? formatDate(row.original.processedAt) : 'N/A'}
                            </span>
                          )
                        }
                      ]}
                      data={paymentDetails.installments}
                      pagination={false}
                    />
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No payment installments received yet</p>
                      <p className="text-sm">Installments will appear here when customers make payments</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3">
                {paymentDetails.status === 'failed' && (
                  <PermissionGate permission="payments:update">
                    <Button
                      onClick={() => handleRetryPayment(paymentDetails.id)}
                      disabled={retryLoading}
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Retry Payment
                    </Button>
                  </PermissionGate>
                )}
                <Button variant="outline" onClick={handleCloseModal}>
                  Close
                </Button>
              </div>
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
}
